/* eslint-disable no-type-assertion/no-type-assertion -- lint to the future batch ignore */
import { useEmberService } from '@qonto/react-migration-toolkit/react/hooks';
import { useEmberIntl } from '@qonto/react-migration-toolkit/react/hooks/providers';
import { EmberServicesProvider } from '@repo/poly-hooks/contexts/ember-services-context.tsx';
import { type PolymorphicSegment, PolymorphicSegmentProvider } from '@repo/poly-hooks/segment';
import { SentryProvider } from '@repo/shared-kit/sentry';
import { ZendeskLocalizationProvider } from '@repo/poly-hooks/zendesk-localization';
import { QueryClientProvider } from '@tanstack/react-query';
import type { PropsWithChildren, ReactNode } from 'react';
import { useMemo } from 'react';
import { I18nProvider } from 'react-aria';
import { IntlProvider } from 'react-intl';
import { defaultRichTextElements } from '@repo/shared-kit/utils';
import { NavigateProvider } from '@repo/poly-hooks/routing';
import { useEmberNavigate } from '@repo/poly-hooks/routing/-ember/get-navigate';
import { FlagsProvider } from '@repo/poly-hooks/flags';
import { createEmberSentryAdapter } from 'register-partners-js/utils/sentry-adapter';
import { createEmberZendeskLocalizationAdapter } from 'register-partners-js/utils/zendesk-localization-adapter';

export function CustomProviders({ children }: PropsWithChildren): ReactNode {
  const intl = useEmberIntl();
  const segment = useEmberService('segment');
  // eslint-disable-next-line @repo/no-use-ember-service-router -- needed for ember services provider
  const emberRouter = useEmberService('router');
  const navigate = useEmberNavigate();
  const launchdarkly = useEmberService('launchdarkly');
  const queryClientManager = useEmberService('queryClientManager');
  const sentry = useEmberService('sentry');
  const sentryAdapter = useMemo(() => createEmberSentryAdapter(sentry), [sentry]);
  const zendeskLocalizationService = useEmberService('zendesk-localization');
  const zendeskLocalization = useMemo(
    () => createEmberZendeskLocalizationAdapter(zendeskLocalizationService),
    [zendeskLocalizationService]
  );

  // Since we only pass a 2-char locale react aria default to en-US
  // making the date format mm/dd/yyyy
  const reactAriaIntlLocale = intl.locale === 'en' ? 'en-GB' : intl.locale;

  return (
    <EmberServicesProvider emberRouter={emberRouter}>
      <SentryProvider sentry={sentryAdapter}>
        <NavigateProvider navigate={navigate}>
          <PolymorphicSegmentProvider segment={segment as unknown as PolymorphicSegment}>
            <FlagsProvider flags={launchdarkly.allFlags}>
              <ZendeskLocalizationProvider zendeskLocalization={zendeskLocalization}>
                <IntlProvider
                  defaultLocale={intl.defaultLocale}
                  defaultRichTextElements={defaultRichTextElements}
                  locale={intl.locale}
                  messages={intl.messages}
                  wrapRichTextChunksInFragment
                >
                  <I18nProvider locale={reactAriaIntlLocale}>
                    <QueryClientProvider client={queryClientManager.queryClient}>
                      {children}
                    </QueryClientProvider>
                  </I18nProvider>
                </IntlProvider>
              </ZendeskLocalizationProvider>
            </FlagsProvider>
          </PolymorphicSegmentProvider>
        </NavigateProvider>
      </SentryProvider>
    </EmberServicesProvider>
  );
}
